/**
 * Color generation via @material/material-color-utilities (HCT).
 *
 * Three outputs, all driven from hex inputs:
 *   1. expandPalette()      — a full M2-stop tonal ramp (50…900) for a single seed,
 *      so the raw --palette-{key}-{tone} CSS vars stay populated for every base color.
 *   2. buildAccentFamily()  — one base color's accent family for a color mode (base,
 *      on, container, container-high, on-container). The "on-" color is HCT tone-picked
 *      for readable contrast. ThemeService writes it as --accent-{key}-{role}.
 *   3. buildSurfaceRoles()  — the shared neutral/surface role set derived from the
 *      theme's surfaceColor base (page bg, chrome, outline). Written as --{role}.
 *
 * v2.1 layering: the per-context layer is gone. Each of the 8 base colors derives its
 * own accent family independently; one surface set is shared. So a theme variant is
 * just a different set of base colors over the same structure.
 */

import {
    argbFromHex, DynamicScheme, Hct, hexFromArgb, TonalPalette, Variant,
} from '@material/material-color-utilities';
import {
    BASE_COLOR_KEYS, HOVER_AFFECTED_ROLES, hoverForCategory, parseTokenColor,
    remappableColorTokens, resolveTokenColor, Theme,
} from './theme-token-catalog';

const M2_TONE_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

// M2 tone 500 corresponds roughly to M3 tone 40 (light) / 80 (dark).
// We map M2 stops onto M3 tones so the perceptual ramp matches Material defaults.
const M2_TO_M3_TONE: Record<number, number> = {
    50: 95, 100: 90, 200: 80, 300: 70, 400: 60,
    500: 40, 600: 35, 700: 30, 800: 20, 900: 10,
};

export type PaletteRamp = Record<number, string>;

/** A 5-seed bundle for one M3 DynamicScheme. For an accent family all five are the
 *  same seed; for the surface set neutral carries the surface hue and error the error. */
export type SeedSet = {
    primary: string;
    secondary: string;
    tertiary: string;
    neutral: string;
    error: string;
};

function _tonal(hex: string): TonalPalette {
    return TonalPalette.fromInt(argbFromHex(hex));
}

/** WCAG relative luminance of a #rrggbb hex. */
function _luminance(hex: string): number {
    const __n = parseInt(hex.slice(1), 16);
    const __lin = (c: number): number => {
        const __s = c / 255;
        return __s <= 0.03928 ? __s / 12.92 : Math.pow((__s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * __lin((__n >> 16) & 255) + 0.7152 * __lin((__n >> 8) & 255) + 0.0722 * __lin(__n & 255);
}

/** Pure black or white — whichever contrasts better with the given background. */
function _onColor(bgHex: string): string {
    const __l = _luminance(bgHex);
    const __onWhite = 1.05 / (__l + 0.05);   // contrast vs #ffffff
    const __onBlack = (__l + 0.05) / 0.05;   // contrast vs #000000
    return __onWhite >= __onBlack ? '#ffffff' : '#000000';
}

/** Full M2-stop tonal ramp (50…900) for a single seed. */
export function expandPalette(seedHex: string): PaletteRamp {
    const __palette = _tonal(seedHex);
    const __ramp: PaletteRamp = {};
    for (const __stop of M2_TONE_STOPS) {
        __ramp[__stop] = hexFromArgb(__palette.tone(M2_TO_M3_TONE[__stop]));
    }
    return __ramp;
}

/**
 * Build an M3 dynamic scheme for one context from its resolved seeds + mode.
 * Explicit palettes keep each seed independent; the scheme still derives
 * accessible on-colors.
 */
function _scheme(seeds: SeedSet, isDark: boolean): DynamicScheme {
    return new DynamicScheme({
        sourceColorHct: Hct.fromInt(argbFromHex(seeds.primary)),
        variant: Variant.TONAL_SPOT,
        contrastLevel: 0,
        isDark,
        primaryPalette:   _tonal(seeds.primary),
        secondaryPalette: _tonal(seeds.secondary),
        tertiaryPalette:  _tonal(seeds.tertiary),
        neutralPalette:   _tonal(seeds.neutral),
        errorPalette:     _tonal(seeds.error),
    });
}

/** Append an alpha byte (a = 0…1) to a #rrggbb hex → #rrggbbaa. a ≥ 1 → hex unchanged. */
function _withAlpha(hex: string, a: number): string {
    if (a >= 0.999) return hex;
    const __byte = Math.round(Math.max(0, Math.min(1, a)) * 255);
    return hex + __byte.toString(16).padStart(2, '0');
}

/**
 * One accent tier (container + container-high + on-container) for a family. `hover`
 * (0…1) controls the hover strength; 1 = full M3 look for both modes.
 *
 * Filled contexts (default): the container is the button's own hover background, so the
 * slider shifts its TONE from the accent (0 = equals the accent bg, no visible change)
 * toward the stock M3 container (1 = full distance). The on-container is re-picked for
 * contrast at any intensity.
 *
 * Ghost contexts (`ghost`): the container is a fill painted over a transparent button,
 * so the slider is the fill's OPACITY — 0 = fully transparent (truly no hover), 1 =
 * solid M3 container. Tone is left at the stock M3 value; only alpha scales.
 */
function _accentTier(
    palette: TonalPalette, baseHex: string, container100: number,
    containerHighTone: number, onContainer100: number, hover: number,
    ghost = false,
): { container: string; containerHigh: string; onContainer: string } {
    const __stockContainer = hexFromArgb(container100);
    const __stockHigh = hexFromArgb(palette.tone(containerHighTone));
    if (hover >= 0.999) {
        return { container: __stockContainer, containerHigh: __stockHigh, onContainer: hexFromArgb(onContainer100) };
    }
    if (ghost) {
        return {
            container:     _withAlpha(__stockContainer, hover),
            containerHigh: _withAlpha(__stockHigh, hover),
            onContainer:   hexFromArgb(onContainer100),
        };
    }
    const __baseTone = Hct.fromInt(argbFromHex(baseHex)).tone;
    const __cTone  = __baseTone + (Hct.fromInt(container100).tone - __baseTone) * hover;
    const __chTone = __baseTone + (containerHighTone - __baseTone) * hover;
    const __container = hexFromArgb(palette.tone(__cTone));
    return {
        container:     __container,
        containerHigh: hexFromArgb(palette.tone(__chTone)),
        onContainer:   _onColor(__container),
    };
}

/**
 * One base color's accent family for a color mode: the seed itself (WYSIWYG in light,
 * M3-lightened in dark), its contrast on-color, and the container/hover tier. Keys are
 * the AccentRole suffix; ThemeService writes them as --accent-{key}-{role} (base →
 * --accent-{key}). `hover` (0…1, default 1) scales the *-container tones toward the seed
 * — 1 = full M3 distance, lower = subtler hover. Single-seed derivation: identical to the
 * old per-context primary tier, so swapping a base color stays behavior-preserving.
 */
export function buildAccentFamily(seedHex: string, isDark: boolean, hover = 1): Record<string, string> {
    const __s = _scheme({ primary: seedHex, secondary: seedHex, tertiary: seedHex, neutral: seedHex, error: seedHex }, isDark);
    const __base = isDark ? hexFromArgb(__s.primary) : seedHex;
    const __containerHighTone = isDark ? 40 : 80;
    const __tier = _accentTier(
        __s.primaryPalette, __base, __s.primaryContainer, __containerHighTone,
        __s.onPrimaryContainer, Math.max(0, Math.min(1, hover)),
    );
    return {
        'base':           __base,
        'on':             isDark ? hexFromArgb(__s.onPrimary) : _onColor(seedHex),
        'container':      __tier.container,
        'container-high': __tier.containerHigh,
        'on-container':   __tier.onContainer,
    };
}

/**
 * The shared neutral/surface role set for a color mode, derived from the theme's
 * surfaceColor base (low-chroma surface tones) with error as the error palette.
 * ThemeService writes these as --{role}. Changing surfaceColor re-tints every surface
 * in proportion. Keys mirror SURFACE_ROLE_KEYS in the catalog.
 */
export function buildSurfaceRoles(surfaceSeedHex: string, errorHex: string, isDark: boolean): Record<string, string> {
    const __s = _scheme(
        { primary: surfaceSeedHex, secondary: surfaceSeedHex, tertiary: surfaceSeedHex, neutral: surfaceSeedHex, error: errorHex },
        isDark,
    );
    return {
        'surface':                hexFromArgb(__s.surface),
        'on-surface':             hexFromArgb(__s.onSurface),
        'surface-variant':        hexFromArgb(__s.surfaceVariant),
        'on-surface-variant':     hexFromArgb(__s.onSurfaceVariant),
        'surface-container-lowest': hexFromArgb(__s.surfaceContainerLowest),
        'surface-container':      hexFromArgb(__s.surfaceContainer),
        'surface-container-high': hexFromArgb(__s.surfaceContainerHigh),
        'background':             hexFromArgb(__s.background),
        'on-background':          hexFromArgb(__s.onBackground),
        'outline':                hexFromArgb(__s.outline),
        'outline-variant':        hexFromArgb(__s.outlineVariant),
    };
}

// ─── Shared CSS-var builders (used by ThemeService :root + the preview inline) ──
// One source of truth so the live preview can never drift from the runtime apply.

/**
 * All colour CSS vars for a theme + mode: per-base-color tonal ramps
 * (--palette-{key}-{stop}) + accent families (--accent-{key}-{role}) + the shared
 * surface set (--{role}) derived from surfaceColor. Returned as name → value.
 */
export function buildThemeColorVars(theme: Theme, isDark: boolean): Record<string, string> {
    const __out: Record<string, string> = {};
    const __hover = (theme.hoverIntensity ?? 100) / 100;
    for (const __key of BASE_COLOR_KEYS) {
        const __seed = theme.baseColors[__key];
        if (!__seed) continue;
        for (const [__stop, __hex] of Object.entries(expandPalette(__seed))) {
            __out[`--palette-${__key}-${__stop}`] = __hex;
        }
        for (const [__role, __hex] of Object.entries(buildAccentFamily(__seed, isDark, __hover))) {
            __out[__role === 'base' ? `--accent-${__key}` : `--accent-${__key}-${__role}`] = __hex;
        }
    }
    const __surfaceSeed = theme.baseColors[theme.surfaceColor] ?? theme.baseColors.neutral;
    for (const [__role, __hex] of Object.entries(buildSurfaceRoles(__surfaceSeed, theme.baseColors.error, isDark))) {
        __out[`--${__role}`] = __hex;
    }
    return __out;
}

/**
 * Per-token semantic colour overrides (--color-*): only tokens whose resolved colour
 * differs from their static _semantic.scss default (a per-category accent swap, a
 * per-token base-color override, a per-category hover intensity, or a transparency) are
 * emitted; the rest are omitted so they fall back to the stylesheet default.
 *
 * Mostly mode-independent (values are var() refs), EXCEPT a per-category hover override:
 * that bakes the actual *-container tone at the category's own intensity (the shared
 * --accent-{key}-container var only carries the GLOBAL hover, so co-tenants of one base
 * color can still differ). That tone is mode-dependent, hence the `isDark` argument.
 */
export function buildSemanticColorOverrides(theme: Theme, isDark = false): Record<string, string> {
    const __out: Record<string, string> = {};
    const __globalHover = (theme.hoverIntensity ?? 100) / 100;
    for (const { name: __name, entry: __entry } of remappableColorTokens()) {
        let __base = resolveTokenColor(theme, __name, __entry, isDark);
        // Per-category hover: if this token resolves to an accent hover-tier role and its
        // category deviates from the global hover, recompute that role's tone at the
        // category's intensity (a baked hex, not the shared var).
        const __catHover = hoverForCategory(theme, __entry.category) / 100;
        if (__catHover !== __globalHover) {
            const __p = parseTokenColor(__base);
            if (__p && __p.kind === 'accent' && HOVER_AFFECTED_ROLES.has(__p.role)) {
                const __seed = theme.baseColors[__p.key];
                if (__seed) __base = buildAccentFamily(__seed, isDark, __catHover)[__p.role] ?? __base;
            }
        }
        const __alpha = theme.semanticAlpha?.[__name];
        if (__alpha != null && __alpha < 100) {
            __out[__entry.cssVar] = `color-mix(in srgb, ${__base} ${__alpha}%, transparent)`;
        } else if (__base !== __entry.default) {
            __out[__entry.cssVar] = __base;
        }
    }
    return __out;
}
